import { useFragment } from '@apollo/client/react';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { getMemberId } from '@trello/authentication';
import { client, optimisticIdManager } from '@trello/graphql';
import { assertMutationSuccess } from '@trello/graphql-error-handling';
import { showFlag } from '@trello/nachos/experimental-flags';
import { calculatePosition, MAX_POS, NULL_POS } from '@trello/position';

import {
  TrelloCreateSmartScheduleOnboardingAiRuleDocument,
  type TrelloCreateSmartScheduleOnboardingAiRuleMutationVariables,
} from './TrelloCreateSmartScheduleOnboardingAiRuleMutation.generated';
import { TrelloSmartScheduleOnboardingMemberAiRulesFragmentDoc } from './TrelloSmartScheduleOnboardingMemberAiRulesFragment.generated';
import { TrelloUpdateSmartScheduleOnboardingAiRuleDocument } from './TrelloUpdateSmartScheduleOnboardingAiRuleMutation.generated';

export type SmartScheduleOnboardingAiRuleType = Extract<
  TrelloCreateSmartScheduleOnboardingAiRuleMutationVariables['type'],
  'PLANNER_SCHEDULING' | 'PLANNER_SELECTION'
>;

interface UpsertSmartScheduleOnboardingAiRuleArgs {
  existingRuleId?: string;
  rule: string;
  type: SmartScheduleOnboardingAiRuleType;
  /**
   * When true, the internal error flag is suppressed so the caller can surface a
   * single consolidated error (used by the batched onboarding save).
   */
  silent?: boolean;
}

interface UpsertSmartScheduleOnboardingAiRuleResult {
  aiRuleId?: string;
  success: boolean;
}

/**
 * Creates or updates an onboarding AI rule while preserving the local planner
 * rule cache behavior used by Smart Schedule onboarding.
 *
 * @returns An upsert callback plus pending state for disabling onboarding UI
 * while the create or update mutation is in flight.
 */
export const useUpsertSmartScheduleOnboardingAiRule = () => {
  const intl = useIntl();
  const memberAri = getMemberId({ returnAri: true });
  const [isSavingRule, setIsSavingRule] = useState(false);

  const { data: member, complete: isMemberComplete } = useFragment({
    fragment: TrelloSmartScheduleOnboardingMemberAiRulesFragmentDoc,
    fragmentName: 'TrelloSmartScheduleOnboardingMemberAiRules',
    from: memberAri ? { __typename: 'TrelloMember', id: memberAri } : null,
  });

  // Read the latest rules straight from the cache when computing the position
  // (rather than a memoized snapshot) so that consecutive creates issued during
  // the batched onboarding save don't land on the same position.
  const getNewRulePosition = useCallback((): number => {
    const cached = client.readFragment({
      id: `TrelloMember:${memberAri}`,
      fragment: TrelloSmartScheduleOnboardingMemberAiRulesFragmentDoc,
      fragmentName: 'TrelloSmartScheduleOnboardingMemberAiRules',
    });

    const sortedPlannerRules = (cached?.aiRules?.edges ?? [])
      .map((edge) => edge.node)
      .sort((a, b) => (a?.position ?? MAX_POS) - (b?.position ?? MAX_POS));

    const nextRulePosition = sortedPlannerRules[0]?.position ?? NULL_POS;

    return calculatePosition(NULL_POS, nextRulePosition);
  }, [memberAri]);

  const showErrorFlag = useCallback(() => {
    showFlag({
      id: 'create-ai-rule-error',
      title: intl.formatMessage({
        id: 'templates.smart_schedule.smart-schedule-error',
        defaultMessage:
          'There was an error scheduling focus time. Please try again.',
        description: 'Error message when scheduling focus time fails',
      }),
      appearance: 'error',
      isAutoDismiss: false,
    });
  }, [intl]);

  /**
   * Saves a Smart Schedule onboarding rule, updating an existing rule when its
   * id is known and creating one otherwise.
   *
   * @returns Whether the save succeeded and, when available, the AI rule id the
   * dialog should retain for future updates.
   */
  const upsertRule = useCallback(
    async ({
      existingRuleId,
      rule,
      type,
      silent,
    }: UpsertSmartScheduleOnboardingAiRuleArgs): Promise<UpsertSmartScheduleOnboardingAiRuleResult> => {
      const trimmedRule = rule.trim();

      if (!memberAri || trimmedRule.length === 0) {
        return { success: false };
      }

      const existingRule = member?.aiRules?.edges
        ?.map((edge) => edge.node)
        .find((node) => node?.id === existingRuleId);

      setIsSavingRule(true);

      try {
        if (existingRuleId) {
          const result = await client.mutate({
            mutation: TrelloUpdateSmartScheduleOnboardingAiRuleDocument,
            variables: {
              aiRuleId: existingRuleId,
              rule: trimmedRule,
            },
            context: { traceId: null },
            optimisticResponse: {
              trello: {
                __typename: 'TrelloMutationApi',
                updateAiRule: {
                  __typename: 'TrelloUpdateAiRulePayload',
                  aiRule: {
                    __typename: 'TrelloAiRule',
                    id: existingRuleId,
                    objectId: existingRule?.objectId ?? '',
                    position: existingRule?.position ?? null,
                    rule: trimmedRule,
                    type,
                  },
                  errors: null,
                  success: true,
                },
              },
            },
          });

          assertMutationSuccess(
            result.data?.trello.updateAiRule,
            result.error,
            'Failed to update rule',
          );

          return {
            aiRuleId:
              result.data?.trello.updateAiRule?.aiRule?.id ?? existingRuleId,
            success: true,
          };
        }

        const optimisticId =
          optimisticIdManager.generateOptimisticId('TrelloAiRule');
        const newRulePosition = getNewRulePosition();

        const result = await client.mutate({
          mutation: TrelloCreateSmartScheduleOnboardingAiRuleDocument,
          variables: {
            position: newRulePosition,
            rule: trimmedRule,
            type,
          },
          context: { traceId: null },
          optimisticResponse: {
            trello: {
              __typename: 'TrelloMutationApi',
              createMemberAiRule: {
                __typename: 'TrelloCreateMemberAiRulePayload',
                errors: null,
                member: {
                  __typename: 'TrelloMember',
                  id: memberAri,
                  aiRules: {
                    __typename: 'TrelloMemberAiRuleConnection',
                    edges: [
                      {
                        __typename: 'TrelloMemberAiRuleEdge',
                        node: {
                          __typename: 'TrelloAiRule',
                          id: `ari:cloud:trello::ai-rule/${optimisticId}`,
                          objectId: optimisticId,
                          position: newRulePosition,
                          rule: trimmedRule,
                          type,
                        },
                      },
                      ...(isMemberComplete
                        ? (member?.aiRules?.edges ?? [])
                        : []),
                    ],
                  },
                },
                success: true,
              },
            },
          },
          update(_cache, { data: mutationData }) {
            const newRule =
              mutationData?.trello?.createMemberAiRule?.member?.aiRules?.edges
                ?.map((edge) => edge.node)
                .find(
                  (node) =>
                    node?.rule === trimmedRule &&
                    node.type === type &&
                    node.position === newRulePosition,
                );

            if (newRule?.objectId && newRule.objectId !== optimisticId) {
              optimisticIdManager.resolveId(optimisticId, newRule.objectId);
            }
          },
        });

        assertMutationSuccess(
          result.data?.trello.createMemberAiRule,
          result.error,
          'Failed to create rule',
        );

        const newRule =
          result.data?.trello.createMemberAiRule?.member?.aiRules?.edges
            ?.map((edge) => edge.node)
            .find(
              (node) =>
                node?.rule === trimmedRule &&
                node.type === type &&
                node.position === newRulePosition,
            );

        return { aiRuleId: newRule?.id, success: true };
      } catch {
        if (!silent) {
          showErrorFlag();
        }

        return { success: false };
      } finally {
        setIsSavingRule(false);
      }
    },
    [
      getNewRulePosition,
      isMemberComplete,
      member?.aiRules?.edges,
      memberAri,
      showErrorFlag,
    ],
  );

  return { isSavingRule, upsertRule };
};
